import {
  ArrowGo,
  Calendar,
  Check,
  Comment,
  EmoFire,
  Gift,
  Heart,
  HeartFilled,
  Pin,
  Plus,
  Share,
  StarFilled,
  UserFilled,
  Users,
} from "@assets"
import { Link, router } from "@inertiajs/react"
import { ArrowBigUp, BarChart2, ShoppingCart, Star } from "lucide-react"
import React, { useEffect, useState } from "react"

import { AuthButton } from "@/components/auth-button"
import { ContentActionsMenu } from "@/components/content-actions-menu"
import AnnouncementFeedItem from "@/components/feed/announcement-item"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useFavorites } from "@/hooks/use-favorites"
import { cn } from "@/lib/utils"
import {
  businessPath,
  eventPath,
  forumPostPath,
  interviewPath,
  jobPath,
  productPath,
  rafflePath,
} from "@/routes"
type FeedItemType =
  | "event"
  | "forum_post"
  | "raffle"
  | "product"
  | "business"
  | "interview"
  | "announcement"
  | "job"

interface BaseFeedItem {
  id: number | string
  type: FeedItemType
  created_at: string
  is_favorited?: boolean
  favorites_count?: number
  source_id: number
  priority_score?: number
  context_tags?: { icon: string; text: string }[]
}

interface EventItem extends BaseFeedItem {
  type: "event"
  title: string
  description?: string | { body?: string }
  start_date: string
  end_date?: string
  venue_name?: string
  address?: string
  is_free: boolean
  price?: number
  capacity?: number
  featured?: boolean
  category?: string
  organizer: {
    id: number
    name: string
    verified?: boolean
  }
  neighborhood?: {
    id: number
    name: string
  }
  city?: {
    id: number
    name: string
  }
  event_attendees: {
    id: number
    status: string
  }[]
  attendees_today?: number
  is_attending?: boolean
  is_favorited?: boolean
}

interface ForumPostItem extends BaseFeedItem {
  type: "forum_post"
  title: string
  content: string | { body?: string }
  author: {
    id: number
    name: string
    verified?: boolean
  }
  category: {
    id: number
    name: string
  }
  forum_answers: {
    id: number
  }[]
  forum_votes: {
    vote_type: string
  }[]
  view_count: number
  is_favorited?: boolean
  is_pinned?: boolean
}

interface RaffleItem extends BaseFeedItem {
  type: "raffle"
  title: string
  description?: string | { body?: string }
  start_date?: string
  end_date: string
  prize_value?: number
  winner_count: number
  max_participants?: number
  sponsor?: {
    id: number
    name: string
    verified?: boolean
    owner_id?: number
  }
  neighborhood?: {
    id: number
    name: string
  }
  city?: {
    id: number
    name: string
  }
  raffle_entries: {
    id: number
  }[]
  image_url?: string
  is_active?: boolean
  is_upcoming?: boolean
  has_ended?: boolean
  participants_count?: number
  is_entered?: boolean
  is_favorited?: boolean
}

interface ProductItem extends BaseFeedItem {
  type: "product"
  name: string
  description?: string | { body?: string }
  price: number
  sale_price?: number
  current_price?: number
  currency?: string
  condition: string
  in_stock?: boolean
  on_sale?: boolean
  business: {
    id: number
    name: string
    verified?: boolean
    owner_id?: number
  }
  category?: {
    id: number
    name: string
  }
  images?: string[]
  is_favorited?: boolean
  in_cart?: boolean
}

interface BusinessItem extends BaseFeedItem {
  type: "business"
  name: string
  description?: string | { body?: string }
  category: string
  owner?:
    | {
        id: number
        name: string
        avatar_url?: string
        verified?: boolean
      }
    | string
    | null
  address?: string
  phone?: string
  website?: string
  verified?: boolean
  rating?: number
  total_reviews?: number
  subscription_tier?: string
  neighborhood?: {
    id: number
    name: string
  }
  city?: {
    id: number
    name: string
  }
  features?: string[]
  images?: string[]
  opening_hours?: unknown
  has_delivery?: boolean
  has_pickup?: boolean
  is_favorited?: boolean
  is_owner?: boolean
}

interface InterviewItem extends BaseFeedItem {
  type: "interview"
  title: string
  interviewee_name?: string
  excerpt?: string
  cover_url?: string | null
  author?: {
    id: number
    name: string
    verified?: boolean
  }
}

interface JobItem extends BaseFeedItem {
  type: "job"
  title: string
  company_name: string
  location_type?: string
  category?: string
  apply_url?: string
  source?: string
  source_name?: string
  excerpt?: string
  expires_at?: string
  author?: {
    id: number
    name: string
    verified?: boolean
  }
  neighborhood?: {
    id: number
    name: string
  }
}

interface AnnouncementItem extends BaseFeedItem {
  type: "announcement"
  content: string | { body?: string }
  author?: {
    id: number
    name: string
    verified?: boolean
  }
  cover_url?: string | null
  video_url?: string | null
}

export type FeedItem =
  | EventItem
  | ForumPostItem
  | RaffleItem
  | ProductItem
  | BusinessItem
  | InterviewItem
  | JobItem
  | AnnouncementItem

export type FeedItemVariant = "feed" | "compact" | "card" | "grid"

interface FeedItemProps {
  item: FeedItem
  isLast?: boolean
  variant?: FeedItemVariant
  showActions?: boolean
  className?: string
}

function isAnnouncement(item: FeedItem): item is AnnouncementItem {
  return item.type === "announcement"
}

function FeedItemComponent({
  item,
  isLast = false,
  variant = "feed",
  showActions = true,
  className,
}: FeedItemProps) {
  const isHighlighted =
    (item.type === "event" && item.featured) ??
    (item.type === "forum_post" && item.is_pinned)

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - date.getTime())
    const diffMinutes = Math.floor(diffTime / (1000 * 60))
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60))
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    if (diffMinutes < 60) return `${diffMinutes}m`
    if (diffHours < 24) return `${diffHours}h`
    if (diffDays < 7) return `${diffDays}d`

    return new Intl.DateTimeFormat("es-ES", {
      day: "numeric",
      month: "short",
    }).format(date)
  }

  const formatEventDate = (dateStr: string) => {
    const date = new Date(dateStr)
    if (Number.isNaN(date.getTime())) return ""

    const parts = new Intl.DateTimeFormat("es-ES", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(date)

    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((p) => p.type === type)?.value ?? ""

    let weekday = get("weekday")
    weekday = weekday.trim()
    weekday = weekday.charAt(0).toUpperCase() + weekday.slice(1)
    if (!weekday.endsWith(".")) weekday = `${weekday}.`

    const day = get("day")
    let month = get("month")
    month = month.replace(/\./g, "").toLowerCase()

    const time = `${get("hour")}:${get("minute")}`

    return `${weekday} ${day} ${month} • ${time}hs.`
  }

  const getAuthor = () => {
    switch (item.type) {
      case "event":
        return item.organizer.name
      case "forum_post":
        return item.author.name
      case "announcement":
        return item.author?.name ?? "Post"
      case "interview":
        return item.author?.name ?? "Entrevista"
      case "job":
        return item.company_name
      case "product":
        return item.business.name
      case "business": {
        const owner = item.owner
        if (typeof owner === "string") {
          return owner ?? item.name
        }
        if (owner && typeof owner === "object") {
          return owner.name ?? item.name
        }
        return item.name
      }
      case "raffle":
        return (
          <span className="inline-flex items-center gap-1">
            {item.sponsor?.name ?? "Sorteo"}
          </span>
        )
      default:
        return "Unknown"
    }
  }

  const getReportType = (): string => {
    const typeMap: Record<FeedItemType, string> = {
      event: "Event",
      forum_post: "ForumPost",
      raffle: "Raffle",
      product: "Product",
      business: "Business",
      interview: "Interview",
      job: "Job",
      announcement: "Announcement",
    }
    return typeMap[item.type] ?? "ForumPost"
  }

  const getAuthorId = (): number => {
    switch (item.type) {
      case "event":
        return item.organizer.id
      case "forum_post":
        return item.author.id
      case "announcement":
        return item.author?.id ?? 0
      case "interview":
        return item.author?.id ?? 0
      case "job":
        return item.author?.id ?? 0
      case "product":
        return item.business?.owner_id ?? 0
      case "business":
        return typeof item.owner === "object" && item.owner ? item.owner.id : 0
      case "raffle":
        return item.sponsor?.owner_id ?? 0
      default:
        return 0
    }
  }

  const getAuthorNameString = (): string => {
    switch (item.type) {
      case "event":
        return item.organizer.name
      case "forum_post":
        return item.author.name
      case "announcement":
        return item.author?.name ?? "Usuario"
      case "interview":
        return item.author?.name ?? "Usuario"
      case "job":
        return item.company_name
      case "product":
        return item.business?.name ?? "Negocio"
      case "business":
        return typeof item.owner === "object" && item.owner
          ? item.owner.name
          : item.name
      case "raffle":
        return item.sponsor?.name ?? "Sponsor"
      default:
        return "Usuario"
    }
  }

  const isVerified = () => {
    switch (item.type) {
      case "business":
        return Boolean(item.verified)
      case "product":
        return Boolean(item.business?.verified)
      case "event":
        return Boolean(item.organizer?.verified)
      case "forum_post":
        return Boolean(item.author?.verified)
      case "announcement":
        return Boolean(item.author?.verified)
      case "interview":
        return Boolean(item.author?.verified)
      case "job":
        return Boolean(item.author?.verified)
      case "raffle":
        return Boolean(item.sponsor?.verified)
      default:
        return false
    }
  }

  const getLink = () => {
    // Safety check: ensure source_id exists before generating links
    if (!item.source_id) {
      item.source_id = Number(item.id)
    }

    switch (item.type) {
      case "event":
        return eventPath(item.source_id)
      case "forum_post":
        return forumPostPath(item.source_id)
      case "raffle":
        return rafflePath(item.source_id)
      case "product":
        return productPath(item.source_id)
      case "business":
        return businessPath(item.source_id)
      case "interview":
        return interviewPath(item.source_id)
      case "job":
        return jobPath(item.source_id)
      case "announcement":
        return `/anuncios/${item.source_id}`
      default:
        return "#"
    }
  }

  const getContent = () => {
    switch (item.type) {
      case "event":
        return typeof item.description === "string"
          ? item.description
          : item.description?.body
      case "forum_post":
        return typeof item.content === "string"
          ? item.content
          : item.content?.body
      case "announcement":
        return typeof item.content === "string"
          ? item.content
          : item.content?.body
      case "interview":
        return item.excerpt ?? ""
      case "job":
        return item.excerpt ?? ""
      case "raffle":
        return typeof item.description === "string"
          ? item.description
          : item.description?.body
      case "product":
        return typeof item.description === "string"
          ? item.description
          : item.description?.body
      case "business":
        return typeof item.description === "string"
          ? item.description
          : item.description?.body
      default:
        return ""
    }
  }

  const getTitle = () => {
    switch (item.type) {
      case "event":
      case "forum_post":
      case "raffle":
        return item.title
      case "interview":
        // Show "Nombre: 'Título'" format if interviewee_name exists
        if (item.interviewee_name) {
          return `${item.interviewee_name}: '${item.title}'`
        }
        return item.title
      case "job":
        return item.title
      case "product":
      case "business":
        return item.name
      default:
        return ""
    }
  }

  const handleShare = async () => {
    try {
      const url = window.location.origin + getLink()
      const title = getTitle() || "Compartir"
      if (navigator.share) {
        await navigator.share({ title, url })
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url)
      }
    } catch {
      // no-op
    }
  }

  const {
    isFavorited: getStoreFavorite,
    setFavorite: setStoreFavorite,
    getFavoritesCount: getStoreCount,
    setFavoritesCount: setStoreCount,
  } = useFavorites()

  // Get stored values from global store (may be undefined if not set)
  const storedFavorite = getStoreFavorite(item.type, item.source_id)
  const storedCount = getStoreCount(item.type, item.source_id)

  // Use store value if available, otherwise fallback to props
  const [localFavorited, setFavorited] = useState<boolean>(
    storedFavorite ?? Boolean(item.is_favorited),
  )
  const [localFavoritesCount, setFavoritesCount] = useState<number>(
    storedCount ?? item.favorites_count ?? 0,
  )

  useEffect(() => {
    // Check store first, then fallback to props
    const currentStoreFavorite = getStoreFavorite(item.type, item.source_id)
    const currentStoreCount = getStoreCount(item.type, item.source_id)

    setFavorited(currentStoreFavorite ?? Boolean(item.is_favorited))
    setFavoritesCount(currentStoreCount ?? item.favorites_count ?? 0)
  }, [
    item.source_id,
    item.type,
    item.is_favorited,
    item.favorites_count,
    getStoreFavorite,
    getStoreCount,
  ])

  const handleFavorite = () => {
    const newValue = !localFavorited
    const newCount = newValue
      ? localFavoritesCount + 1
      : Math.max(0, localFavoritesCount - 1)

    // Update local state
    setFavorited(newValue)
    setFavoritesCount(newCount)

    // Sync with global store
    setStoreFavorite(item.type, item.source_id, newValue)
    setStoreCount(item.type, item.source_id, newCount)

    // Safety check: ensure source_id exists before making requests
    if (!item.source_id) {
      item.source_id = Number(item.id)
    }

    let postPath = ""
    switch (item.type) {
      case "product":
        postPath = `${productPath(item.source_id)}/favorite`
        break
      case "business":
        postPath = `${businessPath(item.source_id)}/favorite`
        break
      case "raffle":
        postPath = `${rafflePath(item.source_id)}/favorite`
        break
      case "event":
        postPath = `${eventPath(item.source_id)}/favorite`
        break
      case "forum_post":
        postPath = `${forumPostPath(item.source_id)}/favorite`
        break
      case "interview":
        postPath = `${interviewPath(item.source_id)}/favorite`
        break
      case "job":
        postPath = `${jobPath(item.source_id)}/favorite`
        break
      default:
        return
    }

    void router.post(
      postPath,
      {},
      {
        preserveScroll: true,
        onError: () => {
          // Revert local state
          setFavorited(!newValue)
          setFavoritesCount(newValue ? Math.max(0, newCount - 1) : newCount + 1)
          // Revert global store
          setStoreFavorite(item.type, item.source_id, !newValue)
          setStoreCount(
            item.type,
            item.source_id,
            newValue ? Math.max(0, newCount - 1) : newCount + 1,
          )
        },
      },
    )
  }
  const getCategoryEmoji = () => {
    if (item.type === "event") {
      const categoryEmojis: Record<string, string> = {
        music: "🎵",
        sports: "⚽",
        food: "🍽️",
        culture: "🎭",
        networking: "🤝",
        family: "👨‍👩‍👧‍👦",
      }
      return categoryEmojis[item.category!] || "📅"
    }
    return null
  }

  const renderMetadata = () => {
    switch (item.type) {
      case "event": {
        return (
          <div className="text-muted-foreground mt-3 flex flex-col gap-1 text-sm sm:flex-row sm:items-center">
            <div className="flex items-center gap-[14px]">
              {item.is_free ? (
                <span className="bg-secondary inline-flex h-[17px] w-fit items-center justify-center rounded-[8px] px-[7px] py-[2px] text-[10px] leading-[14px] tracking-[0.57px] text-white">
                  GRATIS
                </span>
              ) : item.price ? (
                <span className="text-primary bg-card h-[31px] rounded-[10px] px-[8px] py-[3px] text-[18px] leading-[27px] font-semibold tracking-[0.18px]">
                  € {item.price}
                </span>
              ) : null}
              <div className="flex items-center gap-2">
                <Calendar className="fill-muted-foreground h-4 w-4" />
                <span className="text-muted-foreground h-[19px] text-[13px] leading-[20px] tracking-[0.2px]">
                  {formatEventDate(item.start_date)}
                </span>
              </div>
            </div>
            {item.venue_name && (
              <div className="mt-[10px] flex items-start gap-1 sm:mt-0">
                <Pin className="mt-0.5 h-4 w-auto" />
                <div className="flex flex-col md:flex-row md:items-center md:gap-2">
                  <span className="text-muted-foreground text-[13px] leading-[20px] tracking-[0.2px]">
                    {item.venue_name}
                  </span>
                  <button
                    type="button"
                    className="text-chart-3 inline-flex items-center gap-1 text-[12px] leading-[18px] font-medium tracking-[0.18px]"
                    // onClick={(e) => {
                    //   e.preventDefault()
                    //   e.stopPropagation()
                    //   const query = [item.venue_name, item.address].filter(Boolean).join(' ')
                    //   const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
                    //   window.open(url, '_blank', 'noopener,noreferrer')
                    // }}
                  >
                    Ver en Maps
                    <ArrowGo className="fill-chart-3 h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      }
      case "forum_post": {
        return (
          <div className="text-muted-foreground mt-1 flex items-center gap-4 text-xs">
            <span className="bg-primary rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-[0.57px] text-white">
              {item.category.name.toUpperCase()}
            </span>
          </div>
        )
      }
      case "raffle": {
        return (
          <div className="text-muted-foreground mt-1 flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <Calendar className="size-5" />
              <span className="bg-primary rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-[0.57px] text-white">
                {`FINALIZA ${formatEventDate(item.end_date)}`.toUpperCase()}
              </span>
            </div>
            {item.prize_value && (
              <span className="text-primary font-medium">
                €{item.prize_value}
              </span>
            )}
          </div>
        )
      }
      case "product":
        return (
          <div className="text-muted-foreground mt-1 flex items-center gap-4 text-sm">
            <span className="text-primary font-medium">€{item.price}</span>
            <span>{item.condition === "new" ? "Nuevo" : "Usado"}</span>
          </div>
        )
      case "job": {
        const locationLabels: Record<string, string> = {
          remote: "Remoto",
          hybrid: "Híbrido",
          onsite: "Presencial",
        }
        return (
          <div className="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
            {item.location_type && (
              <span className="bg-primary rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-[0.57px] text-white">
                {(
                  locationLabels[item.location_type] ?? item.location_type
                ).toUpperCase()}
              </span>
            )}
            {item.category && (
              <span className="bg-muted text-foreground rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-[0.57px]">
                {item.category.toUpperCase()}
              </span>
            )}
          </div>
        )
      }
      case "business":
        return (
          <div className="text-muted-foreground flex flex-wrap items-center gap-[7px] text-sm">
            <div className="flex items-center">
              <StarFilled className="mr-1 size-5 fill-yellow-400 text-yellow-400" />
              <span className="text-chart-5 text-[13px] font-bold tracking-[0.20px]">
                {Number(item.rating ?? 0).toFixed(1)}
              </span>
              <span className="text-chart-5 bg-card ml-[3px] rounded-[8px] px-[7px] py-[2px] text-[11px] font-medium tracking-[0.26px]">
                {item.total_reviews} reseñas
              </span>
            </div>
            {item.neighborhood?.name && (
              <>
                <span>•</span>
                <div className="text-muted-foreground flex items-center text-[13px] font-normal tracking-[0.20px]">
                  <Pin className="fill-muted-foreground mr-1 size-4" />
                  <span>{item.neighborhood.name}</span>
                </div>
              </>
            )}
          </div>
        )
      default:
        return null
    }
  }

  const renderActions = () => {
    if (isAnnouncement(item)) return null
    const commentCount =
      item.type === "forum_post" ? item.forum_answers?.length || 0 : 0

    return (
      <div className="items-top mt-2 -ml-2 flex gap-1 sm:mt-0">
        {!isAnnouncement(item) && (
          <AuthButton
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full"
            authAction="register"
            onClick={handleFavorite}
            aria-label={
              localFavorited ? "Quitar de favoritos" : "Agregar a favoritos"
            }
          >
            <span className="inline-flex items-center">
              {localFavorited ? (
                <HeartFilled className="text-destructive h-4 w-4 fill-current" />
              ) : (
                <Heart className="text-muted-foreground h-4 w-4 fill-current" />
              )}
              {localFavoritesCount > 0 && (
                <span className="ml-1 text-xs">{localFavoritesCount}</span>
              )}
            </span>
          </AuthButton>
        )}

        {item.type === "forum_post" && (
          <>
            {commentCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-primary hover:bg-primary/10 h-8 rounded-full px-2 transition-colors"
                asChild
              >
                <Link href={getLink()}>
                  <span className="inline-flex items-center">
                    <Comment className="text-muted-foreground h-4 w-4 fill-current" />
                    <span className="ml-1 text-xs">{commentCount}</span>
                  </span>
                </Link>
              </Button>
            )}
          </>
        )}

        {!isAnnouncement(item) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              void handleShare()
            }}
            className="text-muted-foreground hover:text-primary hover:bg-primary/10 h-8 rounded-full"
          >
            <Share className="text-muted-foreground h-4 w-4 fill-current" />
          </Button>
        )}

        <ContentActionsMenu
          contentType={getReportType()}
          contentId={item.source_id}
          authorId={getAuthorId()}
          authorName={getAuthorNameString()}
        />
      </div>
    )
  }

  const content = getContent()
  const title = getTitle()

  // Render compact variant (for lists)
  if (variant === "compact") {
    return (
      <article
        className={cn(
          "cursor-pointer px-0 transition-colors md:px-6",
          !isHighlighted && "hover:bg-muted/30",
          className,
        )}
      >
        <div className="px-[15px] py-3 md:px-6">
          <Link href={getLink()} prefetch className="block">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-foreground line-clamp-2 text-sm leading-tight font-semibold">
                    {title}
                  </h3>
                  <div className="flex flex-col items-end gap-1">
                    {item.type === "event" && item.category && (
                      <Badge variant="outline" className="text-[10px]">
                        {getCategoryEmoji()} {item.category}
                      </Badge>
                    )}
                    {item.type === "raffle" && item.prize_value && (
                      <Badge variant="secondary" className="text-xs">
                        € {item.prize_value}
                      </Badge>
                    )}
                    {item.type === "product" && (
                      <span className="text-primary text-sm font-medium">
                        €{item.price}
                      </span>
                    )}
                  </div>
                </div>
                {renderMetadata()}
              </div>
            </div>
          </Link>
        </div>
        {!isLast && <div className="bg-chart-4 mx-0 h-px md:mx-6" />}
      </article>
    )
  }

  // Render card variant (traditional card layout)
  if (variant === "card") {
    return (
      <article
        className={cn(
          "hover:bg-muted/30 cursor-pointer transition-colors",
          className,
        )}
        onClick={() => router.visit(getLink())}
      >
        <div className="flex items-start px-[15px] pt-2 sm:px-6 md:px-0">
          {item.type === "business" && (
            <Avatar className="mr-[12px] h-[60px] w-[60px] overflow-hidden rounded-full sm:ml-[0px] md:ml-6">
              <AvatarImage
                src={
                  typeof item.owner === "object" && item.owner?.avatar_url
                    ? item.owner.avatar_url
                    : undefined
                }
                alt={
                  typeof item.owner === "object" ? item.owner?.name : undefined
                }
              />
              <AvatarFallback className="bg-card p-1">
                <UserFilled className="fill-chart-2 h-full w-full" />
              </AvatarFallback>
            </Avatar>
          )}
          <div className="sm:px-[15px] md:px-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                {item.type !== "interview" && item.type !== "business" && (
                  <div className="text-muted-foreground flex items-center gap-1 text-sm">
                    <div className="flex h-[19px] text-[13px] leading-[20px] font-medium tracking-[0.2px]">
                      {getAuthor()}
                    </div>
                    {item.type === "forum_post" && (
                      <>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">
                          {formatDate(item.created_at)}
                        </span>
                      </>
                    )}
                  </div>
                )}
                <Link
                  href={getLink()}
                  prefetch
                  className="hover:text-primary transition-colors"
                >
                  <h3 className="text-foreground inline-flex items-center gap-2 text-lg leading-tight font-semibold">
                    {title}
                    {item.type === "business" && item.verified && (
                      <Check className="fill-primary h-5 w-5" />
                    )}
                  </h3>
                </Link>

                {item.type === "business" && (
                  <div className="">{renderMetadata()}</div>
                )}
              </div>
              {item.type === "event" && item.featured && (
                <Badge variant="secondary" className="text-xs">
                  ⭐ Destacado
                </Badge>
              )}
            </div>

            {content && (
              <p className="text-chart-5 text-[13px] leading-[20px] tracking-[0.2px]">
                {content}
              </p>
            )}
            {item.type == "business" && (
              <span className="bg-primary h-[17px] items-center rounded-[8px] px-[7px] py-[2px] text-[9.5px] font-normal tracking-[0.57px] text-white">
                {item.category.toUpperCase()}
              </span>
            )}
            {!isAnnouncement(item) &&
              item.type !== "interview" &&
              item.type !== "business" &&
              renderMetadata()}
          </div>
        </div>
        {showActions && !isAnnouncement(item) && (
          <div className="my-2 px-[15px] sm:px-6">{renderActions()}</div>
        )}
        {!isLast &&
          (isAnnouncement(item) ? (
            <div className="bg-chart-4 mx-0 h-px md:mx-6" />
          ) : (
            <div className="bg-chart-4 mx-0 h-px md:mx-6" />
          ))}
      </article>
    )
  }
  // Render grid variant (for product grids)
  if (variant === "grid") {
    return (
      <Link
        href={getLink()}
        prefetch
        className={cn(
          "group bg-card block overflow-hidden rounded-[10px] shadow-sm transition-shadow duration-200 hover:shadow-md",
          className,
        )}
      >
        <div className="flex h-full flex-col sm:gap-4">
          {/* Imagen arriba */}
          <div className="bg-muted relative h-[94px] w-full flex-shrink-0 overflow-hidden sm:h-56">
            {item.type === "product" &&
              Number(item.price) >
                Number(item.current_price ?? item.sale_price ?? item.price) &&
              Number(item.price) > 0 && (
                <span className="bg-secondary absolute top-[6px] left-[6px] rounded-full px-[6px] py-[2px] text-[10px] font-semibold tracking-[0.57px] text-white">
                  {`${Math.round((1 - Number(item.current_price ?? item.sale_price ?? item.price) / Number(item.price)) * 100)}% OFF`}
                </span>
              )}
            {item.type === "product" &&
            item.images &&
            item.images.length > 0 ? (
              <img
                src={item.images[0]}
                alt={title}
                className="h-full w-full object-cover"
              />
            ) : null}
          </div>

          {/* Información abajo */}
          <div className="flex min-w-0 flex-1 flex-col p-[8px]">
            <h3 className="text-foreground group-hover:text-primary line-clamp-2 text-[14px] font-medium tracking-[0.21px] transition-colors">
              {title}
            </h3>

            {item.type === "product" && (
              <>
                {/* <div className="text-xs text-muted-foreground mt-1">
                  {item.business?.name}
                </div> */}
                {Number(item.price) >
                  Number(
                    item.current_price ?? item.sale_price ?? item.price,
                  ) && (
                  <span className="text-chart-1 text-[13px] font-light tracking-[0.13px] line-through">
                    € {Number(item.price).toFixed(2)}
                  </span>
                )}
                <div className="mt-auto flex items-center justify-between gap-2">
                  <span className="text-primary text-[18px] font-semibold tracking-[0.18px]">
                    €{" "}
                    {Number(
                      item.current_price ?? item.sale_price ?? item.price,
                    ).toFixed(2)}
                  </span>
                  {item.condition && (
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-[0.57px] text-white",
                        item.condition === "new"
                          ? "bg-primary"
                          : "bg-foreground",
                      )}
                    >
                      {item.condition === "new" ? "NUEVO" : "USADO"}
                    </span>
                  )}
                </div>
              </>
            )}

            {item.type === "business" && (
              <>
                <p className="text-chart-5 mt-1 line-clamp-2 text-[13px] leading-[20px] tracking-[0.2px]">
                  {content}
                </p>
                <div className="text-muted-foreground mt-2 flex items-center gap-2 text-xs">
                  {item.rating && item.rating > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                      {item.rating}
                    </span>
                  )}
                  {item.neighborhood && <span>• {item.neighborhood.name}</span>}
                </div>
              </>
            )}
          </div>
        </div>
      </Link>
    )
  }
  const upvotes =
    item.type === "forum_post"
      ? item.forum_votes?.filter((v) => v.vote_type === "up").length || 0
      : 0
  const downvotes =
    item.type === "forum_post"
      ? item.forum_votes?.filter((v) => v.vote_type === "down").length || 0
      : 0
  const score = upvotes - downvotes
  const attendeeCount =
    item.type === "event" && "event_attendees" in item
      ? (item.event_attendees?.filter((a: { status: string }) =>
          ["confirmed", "attended", "going"].includes(a.status),
        ).length ?? 0)
      : 0
  const attendeesToday =
    item.type === "event" && "attendees_today" in item
      ? (item.attendees_today ?? 0)
      : 0
  // Default feed variant (Twitter-like)
  return (
    <article
      className={cn(
        "cursor-pointer px-0 transition-colors",
        !isHighlighted && "hover:bg-muted/30",
      )}
      style={
        isHighlighted ? { background: "var(--gradient-highlight)" } : undefined
      }
    >
      <div className="px-[15px] py-2 md:px-6">
        {isAnnouncement(item) ? (
          <div className="relative">
            <div className="mb-[8px] flex items-center gap-[5px] text-[13px] tracking-[0.2px]">
              <span className="text-foreground font-medium">Anuncio</span>
              <span className="text-muted-foreground font-extrabold">·</span>
              <span className="text-muted-foreground font-normal">
                {formatDate(item.created_at)}
              </span>
            </div>
            <Link href={getLink()} prefetch className="block">
              <AnnouncementFeedItem
                item={{
                  id: item.source_id,
                  content:
                    typeof item.content === "string"
                      ? item.content
                      : (item.content?.body ?? ""),
                  created_at: item.created_at,
                  author: item.author,
                  cover_url: item.cover_url,
                  video_url: item.video_url,
                  images: (
                    item as unknown as {
                      images?: { id: number | string; url: string }[]
                    }
                  ).images,
                  videos: (
                    item as unknown as {
                      videos?: { id: number | string; url: string }[]
                    }
                  ).videos,
                }}
              />
            </Link>
            <div className="absolute top-0 right-0">
              <ContentActionsMenu
                contentType={getReportType()}
                contentId={item.source_id}
                authorId={getAuthorId()}
                authorName={getAuthorNameString()}
              />
            </div>
          </div>
        ) : (
          <Link href={getLink()} prefetch className="block">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="mb-[8px] flex min-h-[32px] items-center justify-between gap-2">
                  {/* Left: author info + highlighted icon */}
                  <div className="flex min-w-0 items-center gap-[5px] text-[13px] tracking-[0.2px]">
                    {isHighlighted && (
                      <span className="bg-card flex h-[32px] w-[32px] flex-shrink-0 items-center justify-center rounded-full">
                        <img src={EmoFire} />
                      </span>
                    )}
                    {item.type === "interview" ? (
                      <>
                        <span className="text-foreground truncate font-medium">
                          Entrevista
                        </span>
                        <span className="text-muted-foreground flex-shrink-0 font-extrabold">
                          ·
                        </span>
                        <span className="text-muted-foreground flex-shrink-0 font-normal">
                          {formatDate(item.created_at)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-foreground truncate font-medium hover:underline">
                          {getAuthor()}
                        </span>
                        {isVerified() && (
                          <Check className="fill-primary size-5 flex-shrink-0" />
                        )}
                        <span className="text-muted-foreground flex-shrink-0 font-extrabold">
                          ·
                        </span>
                        <span className="text-muted-foreground flex-shrink-0 font-normal">
                          {formatDate(item.created_at)}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Right: type-specific stats & actions */}
                  <div className="flex flex-shrink-0 items-center justify-end gap-1">
                    {item.type === "event" && (
                      <div className="flex items-center gap-0.5">
                        {attendeeCount <= 0 ? null : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full"
                          >
                            <span className="inline-flex items-center">
                              <Users className="text-muted-foreground size-5" />
                              <span className="text-muted-foreground ml-1 text-[13px] font-semibold tracking-[0.78px]">
                                {attendeeCount}
                                {attendeesToday > 0 && (
                                  <span className="ml-1 font-medium text-orange-500">
                                    +{attendeesToday} hoy
                                  </span>
                                )}
                              </span>
                            </span>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="default"
                          className="inline-flex h-[32px] w-[32px] items-center"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            router.visit(getLink())
                          }}
                        >
                          <Calendar className="size-5 fill-white" />
                        </Button>
                      </div>
                    )}

                    {item.type === "product" && (
                      <Button
                        size="sm"
                        variant="default"
                        className="inline-flex h-[32px] w-[32px] items-center"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          router.visit(getLink())
                        }}
                      >
                        <ShoppingCart className="h-4 w-4" />
                      </Button>
                    )}
                    {item.type === "raffle" && (
                      <>
                        {item.winner_count > 0 && (
                          <div className="flex items-end gap-0.5">
                            <Gift className="text-muted-foreground size-5" />
                            <span className="text-muted-foreground text-sm font-medium">
                              {item.winner_count}
                            </span>
                          </div>
                        )}
                        {((item.participants_count ?? 0) > 0 ||
                          (item.raffle_entries?.length ?? 0) > 0) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full"
                          >
                            <Users className="text-muted-foreground size-5" />
                            <span className="text-muted-foreground ml-1 text-[13px] font-semibold tracking-[0.78px]">
                              {item.participants_count ??
                                item.raffle_entries?.length ??
                                0}
                            </span>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="default"
                          className="inline-flex h-[32px] w-[32px] items-center"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            router.visit(getLink())
                          }}
                        >
                          <Plus className="size-5" />
                        </Button>
                      </>
                    )}
                    {item.type === "forum_post" && (
                      <>
                        {item.view_count > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-muted-foreground hover:bg-muted-foreground/10 h-8 rounded-full px-1"
                          >
                            <span className="inline-flex items-center">
                              <BarChart2 className="size-5" />
                              <span className="text-muted-foreground ml-1 text-[13px] font-semibold tracking-[0.78px]">
                                {item.view_count}
                              </span>
                            </span>
                          </Button>
                        )}
                        {score !== 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-muted-foreground hover:bg-muted-foreground/10 h-8 rounded-full px-1"
                          >
                            <span className="inline-flex items-center">
                              <ArrowBigUp className="size-5" />
                              <span className="text-muted-foreground ml-1 text-[13px] font-semibold tracking-[0.78px]">
                                {score > 0 ? `+${score}` : score}
                              </span>
                            </span>
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Context Tags */}
                {item.context_tags && item.context_tags.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {item.context_tags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-primary/10 text-primary inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                      >
                        <span>{tag.icon}</span>
                        <span>{tag.text}</span>
                      </span>
                    ))}
                  </div>
                )}

                {title && (
                  <h3 className="text-foreground mt-1 text-base font-semibold">
                    {title}
                  </h3>
                )}

                {content && (
                  <p className="text-chart-5 mt-1 line-clamp-3 text-[13px] leading-[20px] tracking-[0.2px]">
                    {content}
                  </p>
                )}

                {renderMetadata()}
              </div>
            </div>
          </Link>
        )}

        {renderActions()}
      </div>

      {!isLast && <div className="bg-chart-4 mx-0 h-px md:mx-6" />}
    </article>
  )
}

export default React.memo(FeedItemComponent)
